import { Fragment, useState, useRef, useEffect } from "react";
import {
  ViewerApi,
  RendererTypes,
  ENVIRONMENTS,
  Link,
  Media,
  Image,
  Screen,
  ViewerConfig,
  Route,
  Ancestry,
  IAnnotationPopoverData,
  ScrollData,
  PageData,
} from "@bronte/viewer-api/src/main";

import { getURLParams, getAuthToken, getSessionJsURL, getClientId, getTitle } from "./shared/utils";
import { DataProvider } from "./shared/DataProvider";
import { IAnnotationData } from "./shared/types";
import { AnnotationActions, AnnotationTypes, DefaultThemeOptions } from "./shared/constants";

import { getCoursewareStructure } from "./components/Toc";
import AsideBar from "./components/AsideBar";
import { EnvironmentForm } from "./components/EnvironmentForm";
import { Annotations } from "./components/Annotations";
import { ThemeContext } from "./shared/ThemeContext";
import { NavigationBar } from "./components/NavigationBar";

let viewerApi: ViewerApi;
let annotationData: IAnnotationData[] = [];
let currentRenderedId = "";
const isAndroid = navigator.userAgent.match(/Android/i);

const App = () => {
  const urlParams = getURLParams();
  const [renderer] = useState(process.env.VITE_BV_RENDERER as RendererTypes);
  const [packagePath] = useState(urlParams.packagePath || process.env.VITE_PACKAGE_DIR);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const frameElement = useRef(document.createElement("div"));
  const [tocNodeHtml, setTocNodeHtml] = useState("");
  const [screensState, setScreensState] = useState<Screen[]>([]);
  const [renderedId, setRenderedId] = useState("");
  const [showAnnotationMenu, setShowAnnotationMenu] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState<IAnnotationData>(
    {} as IAnnotationData,
  );
  const [showAnnotationsPopover, setShowAnnotationsPopover] = useState(false);
  const [annotationsPopoverData, setAnnotationsPopoverData] = useState<IAnnotationPopoverData>(
    {} as IAnnotationPopoverData,
  );
  const [prevItem, setPrevItem] = useState<Screen>();
  const [nextItem, setNextItem] = useState<Screen>();
  const [imageData, setImageData] = useState<Image | null>(null);
  const [currentTheme, setCurrentTheme] = useState(DefaultThemeOptions);
  const [annotationUpdated, setAnnotationUpdated] = useState(false);
  const [notesPopupDisplay, setNotesPopupDisplay] = useState(false);
  const [flashcardPopupDisplay, setFlashcardPopupDisplay] = useState(false);
  const [pageNumber, setPageNumber] = useState("-");
  const [stitchedLessonRenderId, setStitchedLessonRenderId] = useState("");
  const [parentActivityId, setParentActivityId] = useState<string | undefined>(undefined);

  const isOfflinePackage = !!packagePath;

  const getPrevNextItem = (screens: Screen[], currentId: string): void => {
    const i = screens.findIndex((screen) => screen.elementId === currentId);

    setNextItem(screens[i + 1]);
    setPrevItem(screens[i - 1]);
  };

  const showLoader = (): void => {
    if (isOfflinePackage) {
      setIsLoading(true);
    }
  };

  const hideLoader = (): void => {
    setIsLoading(false);
  };

  /**
   * todo: clean up this code correct error handling
   */
  const init = () => {
    if (isOfflinePackage) {
      loadPage(urlParams.userId);
    } else {
      const screenId = urlParams.screenId;
      const isIESDisabled = urlParams.disableIES;

      if (isIESDisabled) {
        loadPage(urlParams.userId, screenId);
      } else {
        const piSessionConfig = {
          loginSuccessUrl: window.location.href,
          requireLogin: true,
          avoidReInit: true,
        };
        const envURL = getSessionJsURL();
        const clientId = getClientId();
        const sessionJsScript: any = window.document.querySelector("#sessionJSScript");

        sessionJsScript.src = envURL;
        sessionJsScript.onload = function () {
          try {
            setMessage("Authorizing...");

            const piSession = window.piSession;
            piSession.initialize(clientId, piSessionConfig);
            piSession.getToken(async (_status: string, _pearsonToken: string) => {
              setMessage("Loading...");
              loadPage(piSession.userId());
            });
          } catch (e) {
            console.error("Dev Shell: Session js initialize error. Pi Session undefined\n", e);
          }
        };

        sessionJsScript.onerror = () => {
          console.error("Dev Shell: Session js initialize error. Invalid session js url/file");
        };
      }
    }
  };

  useEffect(() => {
    init();

    // Following click and blur listeners are to close the Annotation Popup.
    window.addEventListener("click", (event: MouseEvent) => {
      const annotationPopup = document.querySelector(".annotation-popup");
      if (annotationPopup && !annotationPopup.contains(event.target as HTMLElement)) {
        hideAnnotationMenu();
      }

      const annotationsPopover = document.querySelector(".annotation-icons-popup");
      if (annotationsPopover && !annotationsPopover.contains(event.target as HTMLElement)) {
        hideAnnotationsPopover();
      }
    });
  }, []);

  useEffect(() => {
    const setOnline = (): void => {
      console.log("DEV_SHELL - network status changed to online");
      viewerApi.onNetworkStatusChange(true);
    };
    const setOffline = (): void => {
      console.log("DEV_SHELL - network status changed to offline");
      viewerApi.onNetworkStatusChange(false);
    };

    window.addEventListener("online", setOnline);
    window.addEventListener("offline", setOffline);

    return (): void => {
      window.removeEventListener("online", setOnline);
      window.removeEventListener("offline", setOffline);
    };
  }, []);

  useEffect(() => {
    getPrevNextItem(screensState, renderedId);
  }, [screensState, renderedId]);

  const hideAnnotationMenu = (): void => {
    setShowAnnotationMenu(false);
  };

  const hideAnnotationsPopover = (): void => {
    setShowAnnotationsPopover(false);
  };

  const onAnnotationResponse = (data: IAnnotationData): void => {
    const currentScreenData = screensState.find(
      (screen: Screen) => screen.elementId === currentRenderedId,
    );

    data.screenTitle = getTitle(currentScreenData?.configurationFields || []);
    data.pageId = currentRenderedId;

    const actionType = data.actionType;

    DataProvider.saveAnnotation(data);

    switch (actionType) {
      case AnnotationActions.Add:
        if (
          renderer === RendererTypes.MOBILE &&
          data.annotationType === AnnotationTypes.FlashCard
        ) {
          viewerApi.annotations().clearSelection();
        } else {
          viewerApi.annotations().add(data);
        }

        break;
      case AnnotationActions.Edit:
        viewerApi.annotations().edit(data);
        break;
      case AnnotationActions.Delete:
        viewerApi.annotations().delete(data);
        break;
    }

    setAnnotationUpdated((annotationUpdated) => !annotationUpdated);
  };

  /**
   * method to find the last "ACTIVITY" element's elementId in the ancestry because that is the root activity.
   */
  const fetchParentActivityIdFromRoute = (routeData: Route): void => {
    if (routeData.ancestry) {
      // Clone and reverse the ancestry array to find the last matching item
      const reversedAncestry = [...routeData.ancestry].reverse();
      const activity = reversedAncestry.find((item: Ancestry) => item.elementType === "ACTIVITY");
      if (activity) {
        setParentActivityId(activity.elementId);
      }
    }
  };

  const loadPage = (pearsonUid: string, screenId = ""): void => {
    const { cohortId, deploymentId } = urlParams;
    if (deploymentId && cohortId) {
      const viewerConfig: ViewerConfig = {
        domElement: frameElement.current,
        cohortId,
        deploymentId,
        getAuthToken,
        packagePath,
        renderer,
        pearsonUid,
        env: urlParams.env as ENVIRONMENTS,
        decrypt: (_screenId: string, _filesList: string[], onComplete: () => void) => {
          onComplete();
        },
        isOnline: () => {
          return window.navigator.onLine;
        },
        events: {
          onReady: async () => {
            const {
              tocHtmlNode,
              firstRenderId,
              screens: screenData,
              tocStitchedLessonMap,
            } = (await getCoursewareStructure(viewerApi, isOfflinePackage))();
            setTocNodeHtml(tocHtmlNode);
            setScreensState(screenData);
            setMessage("onReady: Bronte Viewer initialized.");
            // TODO: put the tocStitchedLessonMap as part of the viewerApi instance. This should be changed to a full toc map inline with offline toc map BRNT-29613
            if (!isOfflinePackage) {
              viewerApi.mapWebStitchedLessonToc(tocStitchedLessonMap);
            }
            viewerApi.route(screenId || firstRenderId);

            const themeVariants = await viewerApi.getThemeVariants();

            if (!Array.isArray(themeVariants)) return;

            const defaultThemeVariant = themeVariants?.find(
              (themeVariant) => themeVariant.state === "DEFAULT",
            );

            if (defaultThemeVariant) {
              DefaultThemeOptions.variantId = defaultThemeVariant.variantId;
            }
            viewerApi.updateTheme(DefaultThemeOptions);
          },
          onRouteChange: (data: Route) => {
            if (!isOfflinePackage) {
              fetchParentActivityIdFromRoute(data);
            }
            console.log("Dev Shell: onRouteChange =>", data);
            /* TODO: replace currentRenderedId with renderedId from state.
             Currently using non state local variable "currentRenderedId" because renderedId is coming blank on each route change call.
             */
            if (data?.id !== currentRenderedId) {
              hideAnnotationMenu();

              /**
               * In mobile, we do swipe through multiple screens and those are preloaded, so there is no need to show loader.
               */
              if (renderer !== RendererTypes.MOBILE) {
                showLoader();
              }

              currentRenderedId = data?.id;
              setStitchedLessonRenderId("");
              setRenderedId(currentRenderedId);
            }

            if (isOfflinePackage) {
              annotationData = DataProvider.getAnnotations(data?.id) as IAnnotationData[];
              viewerApi.annotations().load(annotationData);
            }
          },
          onRouteComplete: (data: Route) => {
            console.log("Dev Shell: onRouteComplete =>", data);
            hideLoader();
          },
          onLinkClick: (data: Link) => {
            if (data.type === "ExternalLink") {
              if (isAndroid) {
                alert("An external link is clicked");
              }
              console.log("An external link is clicked: ", data);
            }
          },
          onPageScroll: (data: ScrollData) => {
            console.log("Dev Shell: onPageScroll =>", data);
            if (data.activeScreenId) {
              setStitchedLessonRenderId(data.activeScreenId);
            }
            if (data.dataId) {
              // TODO; persist the dataId to scroll back to this location
            }
          },
          onPageChange: (data: PageData) => {
            setPageNumber(data.pageNumber);
            console.log("Dev Shell: onPageChange =>", data);
          },
          onMediaClick: (data: Media) => {
            if (data.mediaType === "Image") {
              setImageData(data.metadata as Image);
              return;
            }

            // Following will only execute in Mobile in case of Audio and Video.
            console.log(`${data.mediaType} clicked: `, data);
            if (isAndroid) {
              alert(`${data.mediaType} clicked`);
            }
          },
          /** onMediaPlay callback will be triggered from BV when user plays any video or audio in web
           * and can be used to perform any necessary actions if needed by the consumer application
           * one such use case would be when starting TTS by consumer app outside BV screen.
           * @param {Media} data - data from the played media which includes
           * mediaType (audio or video) and other metadata (url, transcript, duration, title etc ) )
           */
          onMediaPlay: (data: Media) => {
            console.log(`${data.mediaType} played: `, data);
          },

          onAnnotationRequest: (annotation: IAnnotationData): void => {
            console.log("onAnnotationRequest: annotation request data", annotation);
            if (!annotation.selectedText) {
              /**
               * This is the case of when user clicks on an existing annotation.
               * It's fetching annotation data from the client.
               */
              const savedAnnotation = (
                DataProvider.getAnnotations(currentRenderedId) as IAnnotationData[]
              )?.find((item: IAnnotationData) => item.annotationId === annotation.annotationId);

              if (savedAnnotation) {
                savedAnnotation.mousePosition = annotation.mousePosition;
                annotation = savedAnnotation;
              }
            }

            setCurrentAnnotation(annotation);
            setShowAnnotationMenu(true);
          },
          onAnnotationIconClick: (data: IAnnotationPopoverData): void => {
            console.log("Dev Shell: onAnnotationIconClick =>", data);

            if (data?.annotations?.length === 1) {
              // [TODO] Instead of annotations[0], fetch from DataProvider.getAnnotationById(annotationId)
              setCurrentAnnotation(data.annotations[0]);
              if (data.annotations[0].noteText) {
                setNotesPopupDisplay(true);
              } else {
                setFlashcardPopupDisplay(true);
              }
            }
            if (data?.annotations?.length > 1) {
              setShowAnnotationsPopover(true);
              setAnnotationsPopoverData(data);
            }
          },
          onPageClick: (): void => {
            console.log("Dev Shell: onPageClick.");

            hideAnnotationMenu();
            hideAnnotationsPopover();
            setNotesPopupDisplay(false);
            setFlashcardPopupDisplay(false);
          },
        },
      };
      viewerApi = new ViewerApi(viewerConfig);
      window.__viewerApi = viewerApi;
    }
  };

  const closeModal = (): void => {
    setImageData(null);
  };
  const idToRender = stitchedLessonRenderId || renderedId;

  return (
    <ThemeContext.Provider value={{ currentTheme, setCurrentTheme }}>
      <h3 className="app-title">App Shell (3rd Party)</h3>
      <div className="app-shell">
        {urlParams.deploymentId && urlParams.cohortId ? (
          <>
            <EnvironmentForm autoOpen={false} />
            <div className="content-wrapper">
              <AsideBar
                annotationUpdated={annotationUpdated}
                tocNodeHtml={tocNodeHtml}
                viewerApi={viewerApi}
                renderedId={idToRender}
                parentActivityId={parentActivityId}
              />

              <div className="content">
                <div className="message">{message}</div>
                <div className="content-frame-container">
                  <div className={`page-loader ${isLoading ? "show" : ""}`}>
                    <div className="spinner"></div>
                  </div>

                  <div
                    className="content-frame"
                    data-disable-check-for-top-level-plugin="true"
                    ref={frameElement}
                  ></div>
                </div>
                <NavigationBar
                  viewerApi={viewerApi}
                  prevItem={prevItem}
                  nextItem={nextItem}
                  currentItem={renderedId}
                  pageNumber={pageNumber}
                  shouldNavigateByPage={isOfflinePackage}
                ></NavigationBar>
              </div>
            </div>
          </>
        ) : (
          <EnvironmentForm autoOpen={true} />
        )}
      </div>

      {imageData && (
        <Fragment>
          <div className="modal-backdrop" onClick={closeModal}></div>
          <div className="modal-content">
            <button className="close-modal" onClick={closeModal}></button>
            <img alt={imageData.alt} className="zoomable-image" src={imageData.src} />
            {imageData.caption && (
              <div
                className="caption"
                dangerouslySetInnerHTML={{ __html: imageData.caption }}
              ></div>
            )}
          </div>
        </Fragment>
      )}

      <Annotations
        annotation={currentAnnotation}
        setCurrentAnnotation={setCurrentAnnotation}
        onAnnotationResponse={onAnnotationResponse}
        notesPopupDisplay={notesPopupDisplay}
        setNotesPopupDisplay={setNotesPopupDisplay}
        flashcardPopupDisplay={flashcardPopupDisplay}
        setFlashcardPopupDisplay={setFlashcardPopupDisplay}
        showAnnotationMenu={showAnnotationMenu}
        hideAnnotationMenu={hideAnnotationMenu}
        annotationsPopoverData={annotationsPopoverData}
        showAnnotationsPopover={showAnnotationsPopover}
        hideAnnotationsPopover={hideAnnotationsPopover}
        renderer={renderer}
      ></Annotations>
    </ThemeContext.Provider>
  );
};

export default App;
